// Provides bridging from us to our client.
// For example, if an Android client is using the web builder in a web view,
// this service would facilitate sending messages from our side to the Android side.

import { FlowModeEnum } from '@/enum/flow_mode';
import { clientPlatform, ClientPlatformEnum } from '@/services/ClientPlatform';
import { captureException } from '@sentry/vue';

// Must be a stringified int.
// Set to -1 since we no longer present the Terms of service or Privacy policy.
const LATEST_TOU_VERSION: string = '-1';

// postMessage without parameter does not work so we will use a placeholder until we need a message.
const PLACE_HOLDER_MESSAGE: Record<string, string> = { message: 'placeholder' };

let isClosetSupported: boolean = false;

enum MessageAction {
  SAVE = 'save',
  EXIT = 'exit',
  LOG_EVENT = 'logEvent',
  LOAD_CLOSET = 'loadCloset',
  LOAD_SELFIE = 'loadSelfie',
  SHARE_OUTFIT = 'shareOutfit',
  REDIRECT_TO_SNAPCHAT = 'redirectToSnapchat',
  PRESENT_APP_DEPRECATION_NOTICE = 'presentAppDeprecationNotice',
}

const postWebMessage = (action: MessageAction, message: any = {}) =>
  window.parent.postMessage({ action, message }, '*');

// Important: The webview consumer has to be compatible with interface changes here
const saveAvatar = (
  avatarJson: Record<string, number>,
  traitOrigins: Record<string, number>,
  flowMode: FlowModeEnum,
  category: string,
) => {
  const message: Record<string, Record<string, number> | string> = {
    avatarJson: avatarJson,
    traitOrigins: traitOrigins,
    exitCategory: category,
  };
  if (flowMode === FlowModeEnum.CREATE) {
    message.touVersion = LATEST_TOU_VERSION;
  }

  switch (clientPlatform) {
    case ClientPlatformEnum.iOS:
      errorCatchClientMessage(() => {
        global?.webkit?.messageHandlers.saveAvatar?.postMessage(message);
      });
      break;
    case ClientPlatformEnum.Android:
      errorCatchClientMessage(() => {
        global?.Android?.saveAvatar(JSON.stringify(message));
      });
      break;
    case ClientPlatformEnum.Safari:
    case ClientPlatformEnum.Web:
      postWebMessage(MessageAction.SAVE, message);
      break;
    default:
      throw new Error(`Unknown client platform in Save: ${clientPlatform}`);
  }
};

const shareOutfit = (avatarJson: Record<string, number>, flowMode: FlowModeEnum) => {
  const message: Record<string, Record<string, number> | string> = {
    avatarJson: avatarJson,
  };
  if (flowMode === FlowModeEnum.CREATE) {
    return;
  }

  switch (clientPlatform) {
    case ClientPlatformEnum.iOS:
      errorCatchClientMessage(() => {
        global?.webkit?.messageHandlers.shareOutfit?.postMessage(message);
      });
      break;
    case ClientPlatformEnum.Android:
      errorCatchClientMessage(() => {
        global?.Android?.shareOutfit(JSON.stringify(message));
      });
      break;
    case ClientPlatformEnum.Safari:
    case ClientPlatformEnum.Web:
    default:
      throw new Error(`Unknown client platform in Share Outfit: ${clientPlatform}`);
  }
};

// Important: The webview consumer has to be compatible with interface changes here
const exitBuilder = (category: string): void => {
  const message: Record<string, string> = { exitCategory: category };

  switch (clientPlatform) {
    case ClientPlatformEnum.iOS:
      // Old client versions may not handle the exit message
      // Note messageHandlers is always defined but exit may not exist on older clients
      // and hence check here to reduce Sentry Errors. Similarily below.
      errorCatchClientMessage(() => {
        const messageHandlers = global!.webkit!.messageHandlers;
        if (messageHandlers?.exit) {
          // postMessage without parameter does not work
          global?.webkit?.messageHandlers.exit?.postMessage(message);
        }
      });
      break;
    case ClientPlatformEnum.Android:
      errorCatchClientMessage(() => {
        const android = global!.Android;
        if (android?.exitWithData) {
          global?.Android?.exitWithData(JSON.stringify(message));
        }
      });
      break;
    case ClientPlatformEnum.Safari:
    case ClientPlatformEnum.Web:
      postWebMessage(MessageAction.EXIT, message);
      break;
    default:
      throw new Error(`Unknown client platform in Exit: ${clientPlatform}`);
  }
};

// Important: The webview consumer has to be compatible with interface changes here
const redirectToSnapchat = (category: string): void => {
  const message: Record<string, string> = {
    category: category,
  };

  switch (clientPlatform) {
    case ClientPlatformEnum.iOS:
      errorCatchClientMessage(() => {
        const messageHandlers = global!.webkit!.messageHandlers;
        if (messageHandlers?.redirectToSnapchat) {
          global?.webkit?.messageHandlers.redirectToSnapchat?.postMessage(message);
        }
      });
      break;
    case ClientPlatformEnum.Android:
      errorCatchClientMessage(() => {
        const android = global!.Android;
        if (android?.redirectToSnapchat) {
          global?.Android?.redirectToSnapchat(JSON.stringify(message));
        }
      });
      break;
    case ClientPlatformEnum.Safari:
    case ClientPlatformEnum.Web:
      postWebMessage(MessageAction.REDIRECT_TO_SNAPCHAT, message);
      break;
    default:
      throw new Error(`Unknown client platform in redirectToSnapchat: ${clientPlatform}`);
  }
};

// Important: The webview consumer has to be compatible with interface changes here
const presentAppDeprecationNotice = (category: string): void => {
  const message: Record<string, string> = {
    category: category,
  };

  switch (clientPlatform) {
    case ClientPlatformEnum.iOS:
      errorCatchClientMessage(() => {
        const messageHandlers = global!.webkit!.messageHandlers;
        if (messageHandlers?.presentAppDeprecationNotice) {
          global?.webkit?.messageHandlers.presentAppDeprecationNotice?.postMessage(message);
        }
      });
      break;
    case ClientPlatformEnum.Android:
      errorCatchClientMessage(() => {
        const android = global!.Android;
        if (android?.presentAppDeprecationNotice) {
          global?.Android?.presentAppDeprecationNotice(JSON.stringify(message));
        }
      });
      break;
    case ClientPlatformEnum.Safari:
    case ClientPlatformEnum.Web:
      postWebMessage(MessageAction.PRESENT_APP_DEPRECATION_NOTICE, message);
      break;
    default:
      throw new Error(`Unknown client platform in presentAppDeprecationNotice: ${clientPlatform}`);
  }
};

// Important: The webview consumer has to be compatible with interface changes here
/**
 * Notifies the client that it should begin loading closet data
 * @returns whether or not closet is supported on the platform
 */
const loadCloset = (): boolean => {
  switch (clientPlatform) {
    case ClientPlatformEnum.iOS: {
      errorCatchClientMessage(() => {
        // Old client versions may not handle the loadCloset message, and similarly for Android.
        const messageHandlers = global!.webkit!.messageHandlers;
        if (messageHandlers?.loadCloset) {
          global?.webkit?.messageHandlers?.loadCloset?.postMessage(PLACE_HOLDER_MESSAGE);
          isClosetSupported = true;
        }
      });
      return isClosetSupported;
    }
    case ClientPlatformEnum.Android:
      errorCatchClientMessage(() => {
        const android = global!.Android;
        if (android?.loadCloset) {
          global?.Android?.loadCloset();
          isClosetSupported = true;
        }
      });
      return isClosetSupported;
    case ClientPlatformEnum.Safari:
    case ClientPlatformEnum.Web:
      postWebMessage(MessageAction.LOAD_CLOSET);
      return true;
    default:
      throw new Error(`Unknown client platform in loadCloset: ${clientPlatform}`);
  }
};

// Important: The webview consumer has to be compatible with interface changes here
const logEvent = (message: Record<string, boolean | string | Record<string, number>>): void => {
  switch (clientPlatform) {
    case ClientPlatformEnum.iOS: {
      errorCatchClientMessage(() => {
        // Old Snapchat apps and Bitmoji apps may not handle the logEvent message, and similarly for Android.
        const messageHandlers = global!.webkit!.messageHandlers;
        if (messageHandlers?.logEvent) {
          global?.webkit?.messageHandlers?.logEvent?.postMessage(message);
        }
      });
      break;
    }
    case ClientPlatformEnum.Android:
      errorCatchClientMessage(() => {
        const android = global!.Android;
        if (android?.logEvent) {
          global?.Android?.logEvent(JSON.stringify(message));
        }
      });
      break;
    case ClientPlatformEnum.Safari:
    case ClientPlatformEnum.Web:
      postWebMessage(MessageAction.LOG_EVENT, message);
      break;
    default:
      throw new Error(`Unknown client platform in logEvent: ${clientPlatform}`);
  }
};

// Important: The webview consumer has to be compatible with interface changes here
const loadSelfie = (): void => {
  switch (clientPlatform) {
    case ClientPlatformEnum.iOS:
      // Old client versions may not handle the loadSelfie message, and similarly for Android.
      errorCatchClientMessage(() => {
        const messageHandlers = global!.webkit!.messageHandlers;
        if (messageHandlers?.loadSelfie) {
          global?.webkit?.messageHandlers.loadSelfie?.postMessage(PLACE_HOLDER_MESSAGE);
        }
      });
      break;
    case ClientPlatformEnum.Android:
      errorCatchClientMessage(() => {
        const android = global!.Android;
        if (android?.loadSelfie) {
          global?.Android?.loadSelfie();
        }
      });
      break;
    case ClientPlatformEnum.Safari:
    case ClientPlatformEnum.Web:
      postWebMessage(MessageAction.LOAD_SELFIE);
      break;
    default:
      throw new Error(`Unknown client platform in loadSelfie: ${clientPlatform}`);
  }
};

const errorCatchClientMessage = (functionName: (param?: any) => void): void => {
  try {
    functionName();
  } catch (e) {
    captureException(e);
  }
};

export default {
  saveAvatar,
  shareOutfit,
  exitBuilder,
  redirectToSnapchat,
  presentAppDeprecationNotice,
  loadCloset,
  logEvent,
  loadSelfie,
};
